/**
 * Decides what THIS session does with a visitor-review announcement: toast, chime, both or neither.
 *
 * Pure and dependency-free so it can be unit-tested without a browser - which matters here because a
 * wrong answer is silent. Nobody notices a ding that never came.
 */

export interface ReviewAlertInput {
    /** Org-year stamped on the announcement by the server. */
    messageOrgYearGU?: string;
    /** This session's own focus org-year. */
    focusOrgYearGU?: string;
    /** Whether this user can open the Visitor Check In / Check Out view at all. */
    hasVisitorViewAccess?: boolean;
    /** This user's "show toast" preference. */
    toastEnabled?: boolean;
    /** This user's "play sound" preference. */
    soundEnabled?: boolean;
}

export interface ReviewAlertDecision {
    toast: boolean;
    chime: boolean;
}

const SILENT: ReviewAlertDecision = { toast: false, chime: false };

function norm(raw: unknown): string {
    return typeof raw === 'string' ? raw.trim().toUpperCase() : '';
}

/**
 * THIS FAILS CLOSED, WHICH IS THE OPPOSITE OF EVERY OTHER FILTER IN VISITOR MANAGEMENT.
 *
 * The org-year refresh stamp, the check-in reason filter and the review-queue fetch all fail OPEN,
 * because a redundant refresh is harmless and a silently dead grid is not. Here, failing open means
 * alerting every session in the school - exactly what this gate exists to stop. So anything missing
 * or undeterminable yields silence.
 *
 * The cost is real and is mitigated elsewhere: the caller logs the resolved PAD access once at mount,
 * so a silent machine can be diagnosed from the console instead of by deploying instrumentation.
 */
export function decideReviewAlert(input: ReviewAlertInput | null | undefined): ReviewAlertDecision {
    if (!input) return SILENT;
    if (input.hasVisitorViewAccess !== true) return SILENT;

    const msg = norm(input.messageOrgYearGU);
    const focus = norm(input.focusOrgYearGU);
    if (msg === '' || focus === '' || msg !== focus) return SILENT;

    return {
        toast: input.toastEnabled !== false,
        chime: input.soundEnabled !== false,
    };
}
